import { PropsWithChildren, useContext, useState } from "react";
import { CellProps, Column, HeaderProps, SortingRule } from "react-table";
import { styled } from "@mui/material/styles";
import { Box, IconButton, Tooltip } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import PersonIcon from "@mui/icons-material/Person";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError, isAxiosError } from "axios";

import { PaginatedTable } from "components/Table/Table";
import { UserProfileContext } from "contexts/UserProfileContext";
import {
  IUserRow,
  User,
  UserType,
  IPostDeactivateUsers,
  PatchedUser
} from "interfaces/user";
import { ToggleUserDialog } from "pages/Users/ToggleUserDialog/ToggleUserDialog";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { AxiosContext } from "contexts/AxiosContext";
import { IApiError } from "interfaces/api-error";
import {
  API_URL_DEACTIVATE_USERS,
  API_URL_USER_PROFILE
} from "constants/api_urls";
import { EditUserDialog } from "components/Users/CreateUserDialog/EditUserDialog";
import { DateCell } from "components/SharedTable/tableRenderers";

export interface ICellKey {
  rowId: number;
  fieldName: string;
}

export interface IUsersTableProps {
  rows: IUserRow[];
  currentPage: number;
  pageSize: number;
  totalRowCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onRowSelectionChange: (selectedRowIds: number[]) => void;
  onChangeSortBy: (sorting: SortingRule<IUserRow>[]) => void;
  isLoading: boolean;
}

const CellContent = styled(Box)`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export function UsersTable({
  rows,
  currentPage,
  onPageChange,
  onPageSizeChange,
  onRowSelectionChange,
  onChangeSortBy,
  totalRowCount,
  pageSize,
  isLoading = false
}: IUsersTableProps): JSX.Element {
  const { currentUserId } = useContext(UserProfileContext);
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [showToggleUserDialog, setShowToggleUserDialog] = useState(false);
  const [userToEdit, setUserToEdit] = useState<IUserRow | undefined>(undefined);
  const [userToToggle, setUserToToggle] = useState<IUserRow | undefined>(
    undefined
  );
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);
  const { axios } = useContext(AxiosContext)!;

  const handleAxiosError = (defaultErrorMessage: string) => (
    err: AxiosError<IApiError>
  ) => {
    addSnackbarToQueue({
      severity: "error",
      message: err?.response?.data?.error || defaultErrorMessage
    });
  };

  const queryClient = useQueryClient();

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ): void => {
    onRowSelectionChange([]);
    onPageChange(page);
  };

  const canToggleUser = (user: IUserRow): boolean => {
    if (user.id === currentUserId) {
      return false;
    }

    if (user.isOnyxUser) {
      return false;
    }

    return user.isEditable;
  };

  const columnDefinitions: Column<IUserRow>[] = [
    {
      Header: "First Name",
      id: "first_name",
      accessor: (data: IUserRow) => data.firstName,
      width: "20%"
    },
    {
      Header: "Last Name",
      id: "last_name",
      accessor: (data: IUserRow) => data.lastName,
      width: "20%"
    },
    {
      Header: "Email",
      id: "email",
      accessor: (data: IUserRow) => data.email,
      width: "30%"
    },
    {
      Header: "Type",
      id: "user_type",
      accessor: (data: IUserRow) => data.userType,
      width: "10%"
    },
    {
      Header: "Role",
      id: "admin_type",
      accessor: (data: IUserRow) => data.role,
      width: "10%"
    },
    {
      Header: "Last Login",
      id: "last_login",
      accessor: (data: IUserRow) => data.lastLogin,
      Cell: (cellProps: CellProps<IUserRow>) =>
        DateCell({ value: cellProps.value })
    },
    {
      Header: "Actions",
      id: "user_actions",
      accessor: (data: IUserRow) => data.userType,
      disableSortBy: true,
      Cell: (data: PropsWithChildren<HeaderProps<IUserRow>>) => {
        const { id, isActive } = data.cell.row.original as IUserRow;
        const toggleButtonLabel = isActive
          ? "Deactivate user"
          : "Activate user";

        return (
          <CellContent>
            <Tooltip title="Edit user">
              <IconButton
                onClick={() => {
                  setShowCreateUserDialog(true);
                  setUserToEdit(data.cell.row.original as IUserRow);
                }}
                aria-label="Edit user"
              >
                <EditOutlinedIcon />
              </IconButton>
            </Tooltip>

            {canToggleUser(data.cell.row.original as IUserRow) && (
              <Tooltip title={toggleButtonLabel}>
                <span className="ignore">
                  <IconButton
                    onClick={() => {
                      setShowToggleUserDialog(true);
                      setUserToToggle(data.cell.row.original as IUserRow);
                    }}
                    aria-label={toggleButtonLabel}
                    disabled={id === currentUserId}
                  >
                    {isActive ? <PersonOffIcon /> : <PersonIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </CellContent>
        );
      },
      width: "10%"
    }
  ];

  const createPatchUserObject = (
    userId: number,
    user: PatchedUser,
    isOnyxUser: boolean
  ): Record<string, any> => {
    const { firstName, lastName, email, userType, roles } = user;

    // Currently the API only allows setting customer-admin or customer-user roles but we may want to support setting Onyx roles in future.
    const rolePrefix = isOnyxUser ? "onyx" : "customer";

    const result = {
      id: userId,
      ...(firstName && { first_name: firstName }),
      ...(lastName && { last_name: lastName }),
      ...(email && { email }),
      ...(userType && { user_type: userType }),
      ...(roles &&
        userType !== UserType.EXTERNAL && {
          roles: [`${rolePrefix}-${roles.toLowerCase()}`]
        })
    };

    return result;
  };

  // PATCH: users/
  const patchUser = useMutation(["patch-user"], {
    mutationFn: (updatedUser: PatchedUser) => {
      return axios
        .patch<User>(
          `${API_URL_USER_PROFILE}${updatedUser.id}`,
          createPatchUserObject(
            updatedUser.id,
            updatedUser,
            userToEdit!.isOnyxUser
          )
        )
        .then(r => r.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      addSnackbarToQueue({
        message: "User was successfully updated.",
        severity: "success"
      });
      setUserToEdit(undefined);
    },
    onError: handleAxiosError("Error updating user.")
  });

  const postDeactivateUsers = useMutation(["deactivate-users"], {
    mutationFn: (userIds: number[]) =>
      axios
        .post<IPostDeactivateUsers>(API_URL_DEACTIVATE_USERS, {
          user_ids: userIds
        })
        .then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      addSnackbarToQueue({
        message: "Selected user was successfully deactivated.",
        severity: "success"
      });
      setUserToToggle(undefined);
    },
    onError: err => {
      let message = "Error deactivating user.";
      if (isAxiosError(err)) {
        message = err?.response?.data?.error;
      }
      addSnackbarToQueue({
        severity: "error",
        message
      });
    }
  });

  const postActivateUsers = useMutation(["activate-users"], {
    mutationFn: (userId: number) =>
      axios
        .post<IPostDeactivateUsers>(`/settings/user_profile/${userId}/activate`)
        .then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      addSnackbarToQueue({
        message: "Selected user was successfully activated.",
        severity: "success"
      });
      setUserToToggle(undefined);
    },
    onError: err => {
      let message = "Error activating user.";
      if (isAxiosError(err)) {
        message = err?.response?.data?.error;
      }
      addSnackbarToQueue({
        severity: "error",
        message
      });
    }
  });

  return (
    <>
      <Box>
        <PaginatedTable
          data={rows}
          columns={columnDefinitions}
          loading={isLoading}
          dataQa="users-table"
          count={totalRowCount}
          page={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onSortingChange={onChangeSortBy}
          onPageSizeChange={(
            event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
          ) => onPageSizeChange(parseInt(event.target.value))}
        />
      </Box>

      {userToEdit && (
        <EditUserDialog
          isOpen={showCreateUserDialog}
          onClose={() => setShowCreateUserDialog(false)}
          onConfirm={async (updatedUser: PatchedUser) => {
            setShowCreateUserDialog(false);
            updatedUser.id = userToEdit.id;
            await patchUser.mutateAsync(updatedUser);
          }}
          user={userToEdit}
        />
      )}

      <ToggleUserDialog
        isOpen={showToggleUserDialog}
        onClose={() => setShowToggleUserDialog(false)}
        onConfirm={async () => {
          setShowToggleUserDialog(false);
          if (userToToggle?.isActive) {
            await postDeactivateUsers.mutateAsync([
              (userToToggle as IUserRow).id
            ]);
          } else {
            await postActivateUsers.mutateAsync((userToToggle as IUserRow).id);
          }
        }}
        user={userToToggle as IUserRow}
      />
    </>
  );
}
